import { memo, useCallback } from "react"
import { CheckCircle, Check, Timer } from "lucide-react"
import { motion } from "motion/react"
import { cn } from "src/utils/Functions"
import { getPriorityColor } from "../../utils"

interface ActionItemData {
  id: string
  title: string
  priority: string
  dueDate: string
  completed?: boolean
}

interface ActionItemProps {
  action: ActionItemData
  onToggle: (id: string) => void
  isCompleted?: boolean
}

export const ActionItem = memo(({ action, onToggle, isCompleted = false }: ActionItemProps) => {
  const handleClick = useCallback(() => onToggle(action.id), [action.id, onToggle])

  if (isCompleted) {
    return (
      <div className="flex items-center gap-2.5 p-1.5 rounded-lg opacity-50">
        <button onClick={handleClick} aria-label={`Mark "${action.title}" as incomplete`} className="shrink-0">
          <CheckCircle className="size-4 text-green-500" aria-hidden="true" />
        </button>
        <p className="text-muted-foreground text-xs line-through">{action.title}</p>
      </div>
    )
  }

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className="flex items-start gap-2.5 p-2.5 rounded-lg hover:bg-muted/50 transition-colors group"
    >
      <button
        onClick={handleClick}
        aria-label={`Mark "${action.title}" as complete`}
        className="shrink-0 mt-0.5 w-4 h-4 rounded border border-muted-foreground/30 flex items-center justify-center group-hover:border-muted-foreground/60 transition-colors"
      >
        <Check className="size-2.5 text-transparent group-hover:text-muted-foreground/40 transition-colors" aria-hidden="true" />
      </button>
      <div className="flex-1 min-w-0">
        <p className="text-foreground text-xs font-medium leading-tight">{action.title}</p>
        <div className="flex items-center gap-2 mt-1">
          <span className={cn("text-[10px] flex items-center gap-0.5", getPriorityColor(action.priority))}>
            <span className="w-1 h-1 rounded-full bg-current" />
            {action.priority}
          </span>
          <span className="text-muted-foreground text-[10px] flex items-center gap-0.5">
            <Timer className="size-2.5" />
            {action.dueDate}
          </span>
        </div>
      </div>
    </motion.div>
  )
})

ActionItem.displayName = "ActionItem"
